import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
    Sparkles,
    ArrowUpRight,
    ArrowRight,
    Wind,
    Flame,
    Waves,
    Leaf,
    Lightbulb,
    ShoppingBag,
    Bot,
} from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Stars, Money } from "@/components/ui-veda/Primitives";
import { DAILY_RITUALS, DOSHAS, describeCombo, pickDailyTip } from "@/constants/dosha";
import { TID } from "@/constants/testIds";

const HERO_IMAGE =
    "https://images.unsplash.com/photo-1726241966580-709b25078be1?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NTJ8MHwxfHNlYXJjaHwyfHxtb3J0YXIlMjBwZXN0bGUlMjBsZWF2ZXN8ZW58MHx8fHwxNzg0MDA1NjY2fDA&ixlib=rb-4.1.0&q=85";

const ELEMENT_ICON = { vata: Wind, pitta: Flame, kapha: Waves };

function greeting() {
    const h = new Date().getHours();
    if (h < 5) return "Rest well";
    if (h < 12) return "Good morning";
    if (h < 17) return "Good afternoon";
    if (h < 21) return "Good evening";
    return "Peaceful night";
}

export default function Home() {
    const { user } = useAuth();
    const [doctors, setDoctors] = useState([]);
    const [products, setProducts] = useState([]);

    useEffect(() => {
        api.get("/doctors").then((r) => setDoctors(r.data)).catch(() => {});
        api.get("/products").then((r) => setProducts(r.data)).catch(() => {});
    }, []);

    const profile = user?.dosha_profile || null;
    const primaryKey = (profile?.primary || "").split("-")[0];
    const info = profile ? describeCombo(profile.primary) : null;
    const Icon = ELEMENT_ICON[primaryKey] || Leaf;
    const rituals = DAILY_RITUALS[primaryKey] || DAILY_RITUALS.vata;
    const tip = pickDailyTip(profile?.primary);

    const topDoctors = useMemo(
        () =>
            [...doctors]
                .sort((a, b) => (b.rating || 0) - (a.rating || 0))
                .slice(0, 4),
        [doctors],
    );
    const featuredProducts = useMemo(() => products.slice(0, 4), [products]);

    return (
        <div data-testid={TID.customerHome}>
            {/* HERO — wellness first */}
            <section className="border-b border-veda-border">
                <div className="veda-container grid items-center gap-10 py-14 md:grid-cols-[1.15fr_1fr] md:gap-16 md:py-20 lg:py-24">
                    <div>
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-brand">
                            <Sparkles size={12} className="mr-1 inline" />
                            {profile
                                ? `${greeting()}${user?.name ? `, ${user.name.split(" ")[0]}` : ""}`
                                : "Sushen Herbs · Ayurveda for daily life"}
                        </p>
                        <h1 className="font-display mt-4 text-4xl leading-[1.05] text-veda-onSurface sm:text-5xl md:text-6xl lg:text-7xl">
                            {profile ? (
                                <>
                                    Your body
                                    <br />
                                    <span className="italic text-veda-brand">
                                        speaks {info?.name}.
                                    </span>
                                </>
                            ) : (
                                <>
                                    Understand your body.
                                    <br />
                                    <span className="italic text-veda-brand">
                                        Live in tune.
                                    </span>
                                </>
                            )}
                        </h1>
                        <p className="mt-5 max-w-xl text-base leading-relaxed text-veda-muted md:text-lg">
                            {profile
                                ? info?.summary
                                : "Ayurveda begins with knowing your unique constitution. Take a 12-question prakriti quiz — your rituals, tips, and remedies will personalise from there."}
                        </p>

                        <div className="mt-8 flex flex-wrap gap-3">
                            {profile ? (
                                <>
                                    <Link
                                        to="/wellness/profile"
                                        data-testid={TID.heroSeeProfile}
                                        className="inline-flex items-center gap-2 bg-veda-inverse px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-veda-onInverse"
                                    >
                                        See my dosha
                                        <ArrowUpRight size={14} strokeWidth={1.75} />
                                    </Link>
                                    <Link
                                        to="/shop"
                                        data-testid={TID.heroBrowseShop}
                                        className="inline-flex items-center gap-2 border border-veda-border bg-veda-surface px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-veda-onSurface"
                                    >
                                        <ShoppingBag size={13} />
                                        Explore remedies
                                    </Link>
                                </>
                            ) : (
                                <>
                                    <Link
                                        to="/wellness/quiz"
                                        data-testid={TID.heroTakeQuiz}
                                        className="inline-flex items-center gap-2 bg-veda-inverse px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-veda-onInverse"
                                    >
                                        Take the dosha quiz
                                        <Sparkles size={13} />
                                    </Link>
                                    <Link
                                        to="/shop"
                                        data-testid={TID.heroBrowseShop}
                                        className="inline-flex items-center gap-2 border border-veda-border bg-veda-surface px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-veda-onSurface"
                                    >
                                        <ShoppingBag size={13} />
                                        Browse the shop
                                    </Link>
                                </>
                            )}
                        </div>
                    </div>

                    <div className="relative">
                        <div className="absolute -inset-4 -z-10 bg-veda-brand/10 md:-inset-6" />
                        <img
                            src={HERO_IMAGE}
                            alt="Ayurvedic mortar and herbs"
                            className="h-[320px] w-full object-cover sm:h-[400px] md:h-[520px]"
                            loading="eager"
                        />
                    </div>
                </div>
            </section>

            {/* WELLNESS FEED — today's tip + rituals + dosha snapshot */}
            <section className="veda-container py-12 md:py-16">
                <div className="mb-8">
                    <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                        Today for you
                    </p>
                    <h2 className="font-display mt-1 text-3xl text-veda-onSurface md:text-4xl">
                        Small acts, deep effect.
                    </h2>
                </div>
                <div className="grid gap-4 md:grid-cols-2 md:gap-6 lg:grid-cols-4">
                    {/* AI Guide entry */}
                    <article
                        data-testid={TID.homeCtaAiGuide}
                        className="border border-veda-brand/40 bg-veda-brand/[0.06] p-6"
                    >
                        <div className="flex items-center gap-2">
                            <Bot size={16} className="text-veda-brand" />
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-brand">
                                Ask the Guide
                            </p>
                        </div>
                        <p className="font-display mt-3 text-2xl leading-snug text-veda-onSurface">
                            Talk to an Ayurvedic AI companion.
                        </p>
                        <p className="mt-2 text-sm leading-relaxed text-veda-muted">
                            Powered by GPT & Gemini. Ask about digestion,
                            sleep, energy, mood — get answers rooted in your
                            dosha.
                        </p>
                        <Link
                            to="/wellness/guide"
                            className="mt-5 inline-flex items-center gap-1.5 bg-veda-inverse px-4 py-2 text-[10px] font-semibold uppercase tracking-[0.14em] text-veda-onInverse"
                        >
                            Start a conversation
                            <ArrowRight size={12} />
                        </Link>
                    </article>

                    {/* Dosha snapshot */}
                    <article
                        data-testid={TID.wellnessDoshaSnapshot}
                        className="border border-veda-border bg-veda-surface p-6"
                    >
                        <div className="flex items-center gap-2">
                            <Icon size={16} className="text-veda-brand" />
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Your prakriti
                            </p>
                        </div>
                        <p className="font-display mt-3 text-2xl text-veda-onSurface">
                            {profile ? info?.name : "Not yet known"}
                        </p>
                        <p className="mt-2 text-sm leading-relaxed text-veda-muted">
                            {profile
                                ? `${info?.element}. Balance-first, ritual-led daily care.`
                                : "Take a 2-minute quiz to reveal your constitution."}
                        </p>
                        <Link
                            to={profile ? "/wellness/profile" : "/wellness/quiz"}
                            className="mt-5 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-veda-brand"
                        >
                            {profile ? "View details" : "Take the quiz"}
                            <ArrowRight size={12} />
                        </Link>
                    </article>

                    {/* Daily tip */}
                    <article
                        data-testid={TID.wellnessDailyTip}
                        className="border border-veda-border bg-veda-surface p-6"
                    >
                        <div className="flex items-center gap-2">
                            <Lightbulb size={16} className="text-veda-brand" />
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Tip for today
                            </p>
                        </div>
                        <p className="font-display mt-3 text-xl leading-snug text-veda-onSurface">
                            {tip}
                        </p>
                        <p className="mt-3 text-xs text-veda-muted">
                            Rotates every day. Rooted in classical Ayurvedic
                            texts — adapted for daily life.
                        </p>
                    </article>

                    {/* Ritual */}
                    <article
                        data-testid={TID.wellnessRitual}
                        className="border border-veda-border bg-veda-surface p-6"
                    >
                        <div className="flex items-center gap-2">
                            <Leaf size={16} className="text-veda-brand" />
                            <p className="text-[10px] font-semibold uppercase tracking-[0.22em] text-veda-muted">
                                Try today's ritual
                            </p>
                        </div>
                        <p className="mt-3 text-[10px] font-semibold uppercase tracking-[0.16em] text-veda-brand">
                            {rituals[0].time}
                        </p>
                        <p className="font-display mt-1 text-xl text-veda-onSurface">
                            {rituals[0].title}
                        </p>
                        <p className="mt-2 text-sm leading-relaxed text-veda-muted">
                            {rituals[0].body}
                        </p>
                        {profile && (
                            <Link
                                to="/wellness/profile"
                                className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold uppercase tracking-[0.14em] text-veda-brand"
                            >
                                See all rituals <ArrowRight size={12} />
                            </Link>
                        )}
                    </article>
                </div>
            </section>

            {/* MEET A VAIDYA */}
            {topDoctors.length > 0 && (
                <section className="border-t border-veda-border bg-veda-surface2/40">
                    <div className="veda-container py-12 md:py-16">
                        <div className="mb-6 flex items-baseline justify-between">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-muted">
                                    Talk to a
                                </p>
                                <h2 className="font-display mt-1 text-3xl text-veda-onSurface md:text-4xl">
                                    Certified Vaidya
                                </h2>
                            </div>
                            <Link
                                to="/shop"
                                className="text-xs font-medium text-veda-brand underline underline-offset-2"
                            >
                                See all
                            </Link>
                        </div>
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                            {topDoctors.map((d) => (
                                <Link
                                    key={d.doctor_id}
                                    to={`/doctor/${d.doctor_id}`}
                                    data-testid={TID.doctorCard(d.doctor_id)}
                                    className="group block border border-veda-border bg-veda-surface transition-colors hover:border-veda-borderStrong"
                                >
                                    <div className="aspect-square w-full overflow-hidden bg-veda-surface2">
                                        <img
                                            src={d.avatar_url}
                                            alt={d.name}
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-3">
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-brand">
                                            {d.specialty}
                                        </p>
                                        <p className="font-display mt-1 line-clamp-1 text-base text-veda-onSurface">
                                            {d.name}
                                        </p>
                                        <div className="mt-2 flex items-center justify-between text-xs text-veda-muted">
                                            <Stars value={d.rating} size={11} />
                                            <span>
                                                <Money value={d.consultation_fee} />
                                            </span>
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* FEATURED REMEDIES */}
            {featuredProducts.length > 0 && (
                <section className="border-t border-veda-border">
                    <div className="veda-container py-12 md:py-16">
                        <div className="mb-6 flex items-baseline justify-between">
                            <div>
                                <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-muted">
                                    Featured
                                </p>
                                <h2 className="font-display mt-1 text-3xl text-veda-onSurface md:text-4xl">
                                    Remedies for daily balance
                                </h2>
                            </div>
                            <Link
                                to="/shop"
                                className="text-xs font-medium text-veda-brand underline underline-offset-2"
                            >
                                Full shop
                            </Link>
                        </div>
                        <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 md:grid-cols-4">
                            {featuredProducts.map((p) => (
                                <Link
                                    key={p.product_id}
                                    to={`/product/${p.product_id}`}
                                    data-testid={TID.productCard(p.product_id)}
                                    className="group block border border-veda-border bg-veda-surface transition-colors hover:border-veda-borderStrong"
                                >
                                    <div className="aspect-[4/5] w-full overflow-hidden bg-veda-surface2">
                                        <img
                                            src={p.image_url}
                                            alt={p.name}
                                            className="h-full w-full object-cover transition-transform duration-500 group-hover:scale-[1.03]"
                                            loading="lazy"
                                        />
                                    </div>
                                    <div className="p-3">
                                        <p className="text-[10px] font-semibold uppercase tracking-[0.18em] text-veda-muted">
                                            {p.company ? `${p.company} · ` : ""}
                                            {p.category}
                                        </p>
                                        <p className="font-display mt-1 line-clamp-2 min-h-[42px] text-[15px] text-veda-onSurface">
                                            {p.name}
                                        </p>
                                        <div className="mt-2 flex items-center justify-between">
                                            <p className="text-sm font-semibold text-veda-onSurface">
                                                <Money value={p.price} />
                                            </p>
                                            {p.rating > 0 && (
                                                <Stars value={p.rating} size={11} />
                                            )}
                                        </div>
                                    </div>
                                </Link>
                            ))}
                        </div>
                    </div>
                </section>
            )}

            {/* MISSION STRIP */}
            <section className="border-t border-veda-border bg-veda-inverse text-veda-onInverse">
                <div className="veda-container py-14 md:py-20">
                    <div className="mx-auto max-w-3xl text-center">
                        <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-veda-onInverse/60">
                            Our promise
                        </p>
                        <h2 className="font-display mt-3 text-3xl md:text-4xl">
                            Understand your body better today
                            <br />
                            <span className="italic">than you did yesterday.</span>
                        </h2>
                        <p className="mt-5 text-sm leading-relaxed text-veda-onInverse/70 md:text-base">
                            Sushen Herbs is not a store. It's a daily practice.
                            We teach before we sell, we heal before we
                            prescribe, and we listen before we advise.
                        </p>
                        {!profile && (
                            <Link
                                to="/wellness/quiz"
                                className="mt-8 inline-flex items-center gap-2 bg-veda-onInverse px-5 py-3 text-xs font-semibold uppercase tracking-[0.16em] text-veda-inverse"
                            >
                                Begin with your dosha <Sparkles size={13} />
                            </Link>
                        )}
                    </div>
                </div>
            </section>
        </div>
    );
}
